import { iaRa, kebabToReadable } from 'lib/helpers/utility';
import { chartIdOf, chartOf, practiceIdOf, withoutQuery } from 'lib/helpers/viewportPath';

import PATIENT_CHART_SIDEBAR_ITEMS from 'components/practice/charts/PatientChart/components/PatientChartSidebar/lib/sidebarItems';

export const MAX_DOCK_TABS = 8;

export const chartNameOf = (chart) => `${chart?.f_name || ''} ${chart?.l_name || ''}`.trim();

export const chartSectionOf = (path) => {
  const section = chartOf(path)?.section || '';
  return PATIENT_CHART_SIDEBAR_ITEMS.find((item) => item.path === section)?.label || 'Overview';
};

const pageNameOf = (path) =>
  kebabToReadable(withoutQuery(path).split('/').filter(Boolean).pop() || 'Page', {
    sentenceCase: true
  });

// `practice` labels a tab in a practice: its name (`practiceName`), or its number until that is known.
export const describeTab = (tab, { findPage, practiceNameOf, charts, activeId }) => {
  const isActive = tab.id === activeId;
  const practiceId = practiceIdOf(tab.path);
  const practiceName = practiceId ? practiceNameOf(practiceId) || null : null;
  const practice = practiceId ? practiceName || `Practice #${practiceId}` : null;
  const inPractice = { practiceId, practiceName, practice };
  const chartId = chartOf(tab.path)?.chartId;
  if (chartId) {
    // An identity verified in one practice never names a tab of the same id in another.
    const known = charts[chartId] || null;
    const chart =
      known && (known.practice_id == null || known.practice_id === practiceId) ? known : null;
    return {
      ...tab,
      ...inPractice,
      isActive,
      kind: 'chart',
      chartId,
      chart,
      name: chartNameOf(chart) || 'Patient chart',
      group: 'Chart',
      where: chartSectionOf(tab.path)
    };
  }
  const page = findPage(withoutQuery(tab.path));
  return {
    ...tab,
    ...inPractice,
    isActive,
    kind: 'page',
    page,
    name: page?.name || pageNameOf(tab.path),
    icon: page?.icon || 'layers-mono',
    group: page && page.group !== page.name ? page.group : null,
    where: tab.label
  };
};

export const makeTabId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// How many history entries remember their tab; older ones fall back to the active tab.
const MAX_REMEMBERED_ENTRIES = 200;

const rememberEntry = (entries, key, id) => {
  if (!key || entries?.[key] === id) return entries || {};
  const next = Object.entries({ ...entries, [key]: id });
  return Object.fromEntries(next.slice(-MAX_REMEMBERED_ENTRIES));
};

// Earlier saves could repeat a page (two tabs left on it), so each saved page is restored once.
export const initTabs = (savedPaths, current, label, pageOf, key = null) => {
  const savedPages = iaRa(savedPaths).filter((path) => typeof path === 'string' && !chartOf(path));
  const saved = [...new Set(savedPages)]
    .slice(0, MAX_DOCK_TABS)
    .map((path) => ({ id: makeTabId(), path, label: null }));
  // By page when the sidebar knows it, else by path (its pages may not have loaded yet).
  const currentPage = pageOf(current);
  const match = saved.find((tab) =>
    currentPage
      ? pageOf(tab.path) === currentPage
      : withoutQuery(tab.path) === withoutQuery(current)
  );
  const active = { id: match?.id || makeTabId(), path: current, label };
  return {
    tabs: match ? saved.map((tab) => (tab === match ? active : tab)) : [...saved, active],
    activeId: active.id,
    entries: rememberEntry({}, key, active.id)
  };
};

const hasTab = (state, id) => !!id && state.tabs.some((tab) => tab.id === id);

const isSameChart = (path, other) =>
  chartIdOf(path) === chartIdOf(other) && practiceIdOf(path) === practiceIdOf(other);

const isSamePage = (path, other, pageOf) => {
  const page = pageOf?.(path);
  return page ? pageOf(other) === page : withoutQuery(path) === withoutQuery(other);
};

// Where a navigation that names no tab lands when it must not overwrite the tab the user is in:
// a chart already open in another tab is reused there, and a restored dock's first arrival goes to
// a tab on that page, else to a new tab while there is room.
const landingOf = (state, path, pageOf) => {
  const isChart = !!chartIdOf(path);
  const chartTab =
    isChart && state.tabs.find((tab) => tab.id !== state.activeId && isSameChart(tab.path, path));
  if (chartTab) return { tabId: chartTab.id };
  if (!state.isRestored) return null;
  const pageTab = state.tabs.find((tab) =>
    isChart
      ? isSameChart(tab.path, path)
      : !chartIdOf(tab.path) && isSamePage(path, tab.path, pageOf)
  );
  if (pageTab) return { tabId: pageTab.id };
  return state.tabs.length < MAX_DOCK_TABS ? { tabId: makeTabId(), isNewTab: true } : null;
};

/**
 * The user arrived somewhere: decide which tab it happened in and move that tab there. A
 * history entry seen before goes back to the tab it was opened in, so Back and Forward return
 * to the right tab. A new entry goes to the tab the navigation asked for (`tabId`; `isNewTab`
 * opens it), else to the active tab, where everything the user opens lands, unless `landingOf`
 * finds it a better one. `pageOf` maps a path to its sidebar page.
 */
export const arrive = (state, arrival, pageOf = null) => {
  const { key, path, label, closeId } = arrival;
  const seen = !!key && key in (state.entries || {});
  const { tabId, isNewTab } =
    (!seen && !arrival.tabId && landingOf(state, path, pageOf)) || arrival;
  let next = state.isRestored ? { ...state, isRestored: false } : state;

  if (!seen && closeId && closeId !== tabId) {
    next = { ...next, tabs: next.tabs.filter((tab) => tab.id !== closeId) };
  }
  if (!seen && isNewTab && tabId && !hasTab(next, tabId)) {
    next = { ...next, tabs: [...next.tabs, { id: tabId, path, label }] };
  }

  const remembered = seen ? state.entries[key] : null;
  const active = next.tabs.find((tab) => tab.id === next.activeId) || next.tabs[0];
  const id =
    [!seen && tabId, remembered].find((candidate) => hasTab(next, candidate)) || active?.id;
  if (!id) return state;

  const target = next.tabs.find((tab) => tab.id === id);
  const unchanged =
    next === state &&
    state.activeId === id &&
    target.path === path &&
    target.label === label &&
    state.entries?.[key] === id;
  if (unchanged) return state;

  return {
    ...next,
    activeId: id,
    tabs: next.tabs.map((tab) => (tab.id === id ? { ...tab, path, label } : tab)),
    entries: rememberEntry(next.entries, key, id)
  };
};

// The caller navigates to the returned tab; the closed tab leaves the dock only once the user
// arrives there (a page with unsaved work may stop them).
export const closeTab = (state, id) => {
  const index = state.tabs.findIndex((tab) => tab.id === id);
  if (index === -1 || state.tabs.length <= 1) return { state, target: null };
  const tabs = state.tabs.filter((tab) => tab.id !== id);
  if (state.activeId !== id) return { state: { ...state, tabs }, target: null };
  return { state, target: tabs[Math.min(index, tabs.length - 1)] };
};

export const closeOtherTabs = (state, id) => ({
  ...state,
  tabs: state.tabs.filter((tab) => tab.id === id),
  activeId: id
});

// What the account keeps: each page tab's page (its home, not where it was left), once; no charts.
export const savedTabPaths = (tabs, pageOf) => [
  ...new Set(
    iaRa(tabs)
      .filter((tab) => !chartOf(tab.path))
      .map((tab) => pageOf(tab.path) || withoutQuery(tab.path))
  )
];
